import { useState, useMemo, useRef, useEffect } from "react";
import { Button, Modal, Input, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { CohortCardForArm } from "../CohortCard";
import {
  addDpArm,
  updateArm,
} from "src/DigitalProtocolModule/utils/ajax-proxy";
import styles from "./AddArmCohortModal.module.scss";
import "./AddArmCohortModal.scss";

interface AddArmCohortModalProps {
  visible: boolean;
  armData?: Digital_Protocol_Arm_Types.Arm;
  doCancel: () => void;
  afterSubmit: () => void;
}

export function AddArmCohortModal({
  visible,
  armData,
  doCancel,
  afterSubmit,
}: AddArmCohortModalProps) {
  const trial_uuid = localStorage.getItem("trial-id");

  const cohortsRef = useRef<any[]>([]);

  const ERROR_MSGS = {
    arm_title_req: "Arm Name is required.",
  };

  const [isSaving, setIsSaving] = useState(false);
  const [armTitle, setArmTitle] = useState<string>(armData?.arm_title || "");
  const [armDescription, setArmDescription] = useState<string>(
    armData?.arm_description || ""
  );
  const [cohortList, setCohortList] = useState<
    Digital_Protocol_Arm_Types.Cohort[]
  >(armData?.cohorts ? [...armData?.cohorts] : []);
  const [error, setError] = useState({
    arm_title: "",
    cohorts: "",
  });

  const cohortListNotDeleted = useMemo(
    () => cohortList.filter((item) => !item.del_flg),
    [cohortList]
  );

  const doValidation = () => {
    let isValid = true;
    let isCohortInEdit = false;
    let tmpError = { ...error };
    if (!Boolean(armTitle.trim())) {
      tmpError.arm_title = ERROR_MSGS.arm_title_req;
      isValid = false;
    } else {
      tmpError.arm_title = "";
    }

    cohortsRef.current.forEach((cohortComponent) => {
      if (cohortComponent?.doValidation) {
        const [isCohortValid, isEditingCohort] = cohortComponent.doValidation();
        isValid = isValid && isCohortValid && !isEditingCohort; // consider as invalid if any cohort is in edit
        isCohortInEdit = isCohortInEdit || isEditingCohort;
      }
    });
    tmpError.cohorts = isCohortInEdit ? "Cohorts are being edited" : "";

    setError(tmpError);

    return isValid;
  };

  const handleCohortAdd = () => {
    // no need to add more empty record if already exist
    if (
      cohortList.length > 0 &&
      cohortList[0].cohort_name === "" &&
      cohortList[0].cohort_description === ""
    ) {
      return;
    }

    setCohortList([
      {
        cohort_name: "",
        cohort_description: "",
        del_flg: false,
      },
      ...cohortList,
    ]);
  };

  // save in edit mode
  const handleCohortUpdate = (
    cohort: Digital_Protocol_Arm_Types.Cohort,
    index: number
  ) => {
    let tmpCohortList = cohortList;
    tmpCohortList[index] = {
      ...tmpCohortList[index],
      ...cohort,
    };
    setCohortList([...tmpCohortList]);
  };

  // cancel in edit mode
  const handleCohortCancel = (index: number) => {
    let tmpCohortList = cohortList;
    if (!tmpCohortList[index].cohort_name) {
      tmpCohortList.splice(index, 1);
      setCohortList([...tmpCohortList]);
    }
  };

  const handleCohortDelete = (index: number) => {
    let tmpCohortList = cohortList;
    if (tmpCohortList[index].cohort_id) {
      tmpCohortList[index].del_flg = true;
    } else {
      tmpCohortList.splice(index, 1);
    }
    setCohortList([...tmpCohortList]);
  };

  const handleSubmit = async () => {
    const isValid = doValidation();
    if (!isValid) {
      return;
    }

    const payload = {
      arm_id: armData?.arm_id || undefined,
      arm_title: armTitle.trim(),
      arm_description: armDescription.trim(),
      trial_uuid,
      cohorts: cohortList,
    };

    try {
      setIsSaving(true);
      const res = armData ? await updateArm(payload) : await addDpArm(payload);
      if (res.error) {
        if (res.error.includes("arm title is duplicate")) {
          message.error("Arm name already exists. Please enter a unique name.");
        } else if (res.error.includes("cohort name is duplicate")) {
          message.error(
            "Cohort name already exists. Please enter a unique name."
          );
        } else {
          message.error(
            `Failed to ${armData?.arm_id ? "edit" : "create"} Arm.`
          );
        }
      } else {
        message.success(
          `Arm ${armData?.arm_id ? "edited" : "created"} successfully.`
        );
        afterSubmit();
      }
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    cohortsRef.current = cohortsRef.current.slice(0, cohortList.length);
  }, [cohortList]);

  return (
    <Modal
      visible={visible}
      title={`${armData ? "Edit" : "Add"} Arm & Cohort`}
      onCancel={doCancel}
      footer={[
        <Button key="back" onClick={doCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          style={{
            backgroundColor: "#ca4a04",
            color: "#fff",
            borderColor: "#ca4a04",
          }}
          onClick={handleSubmit}
          loading={isSaving}
        >
          Submit
        </Button>,
      ]}
      wrapClassName="add-arm-modal-root"
      // centered
    >
      <div className={`${styles.body} add-arm-modal`}>
        <div>
          <label>
            Arm Name <span className={styles.required}>*</span>
          </label>
          <Input
            value={armTitle}
            onChange={(e) => setArmTitle(e.target.value)}
            onBlur={(e) => {
              if (!Boolean(e.target.value.trim())) {
                setError({
                  ...error,
                  arm_title: ERROR_MSGS.arm_title_req,
                });
              } else {
                setError({
                  ...error,
                  arm_title: "",
                });
              }
            }}
            maxLength={50}
          />
          <span className={styles.errorMsg}>{error.arm_title}</span>
        </div>

        <div>
          <label>Description</label>
          <Input
            value={armDescription}
            onChange={(e) => setArmDescription(e.target.value)}
            maxLength={500}
          />
        </div>

        <div className={styles.cohortContainer}>
          <div className={styles.title}>
            <span>
              Cohorts{" "}
              {cohortListNotDeleted?.length > 0 &&
                `(${cohortListNotDeleted?.length})`}
            </span>
            <Button
              className={styles.btn}
              type="primary"
              onClick={handleCohortAdd}
            >
              <PlusOutlined />
              Add Cohort
            </Button>
          </div>
          <div className={styles.errorMsg}>{error.cohorts}</div>

          <div className={styles.list}>
            {cohortList.map((item, index) => {
              if (item.del_flg) {
                cohortsRef.current[index] = null;
                return "";
              } else {
                return (
                  <CohortCardForArm
                    cohort={item}
                    cohortIndex={index}
                    listForDuplicationCheck={cohortList}
                    handleSaveForEdit={handleCohortUpdate}
                    handleCancelForEdit={handleCohortCancel}
                    handleDel={handleCohortDelete}
                    ref={(el) => (cohortsRef.current[index] = el)}
                    key={item.cohort_name}
                  />
                );
              }
            })}
          </div>
        </div>
      </div>
    </Modal>
  );
}
